import {Observer} from "./observers/Observer";
import {Subject} from "./Subject";
import {AudioEngine} from "./audio/AudioEngine";
import {StreamTokenService} from "./audio/StreamTokenService";
import {MediaSessionController} from "./integration/MediaSessionController";
import {QueueManager} from "./playback/QueueManager";
import {
    LoadingState,
    PausedState,
    PlayingState,
    PlayerState as MachinePlayerState,
    StoppedState,
    StateContext
} from "./playback/PlayerStateMachine";
import {
    NoneLoopDecorator,
    PlaylistLoopDecorator,
    PlayerStrategy,
    ShufflePlayerStrategy,
    SimplePlayerStrategy,
    StrategyLoopDecorator,
    TrackLoopDecorator
} from "./strategy/PlayerStrategy";
import {PlaybackControl, TrackDTO} from "./types";
import {
    BUFFER_POLL_INTERVAL_MS,
    BUFFER_STALL_POLLS,
    HEARTBEAT_INTERVAL_MS,
    MAX_TOKEN_RETRIES,
    PREVIOUS_TRACK_THRESHOLD_SEC,
    TOKEN_RETRY_DELAY_MS
} from "./constants";

export class Player implements Subject, PlaybackControl, StateContext {
    private static uniqueInstance: Player | null = null;

    public readonly engine = new AudioEngine();

    private readonly tokenService = new StreamTokenService();
    private readonly queueManager = new QueueManager();
    private readonly mediaSession: MediaSessionController;

    private observers: Observer[] = [];

    private _state: MachinePlayerState;
    private _baseStrategy: PlayerStrategy;
    private _strategy: StrategyLoopDecorator;

    private loadGeneration = 0;
    private pendingTrackId: number | null = null;
    private lastToken: string | null = null;
    private tokenRetries = 0;

    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private bufferTimer: ReturnType<typeof setInterval> | null = null;
    private lastPosition = 0;
    private stalledPolls = 0;

    private volumeLevel = 1;

    constructor() {
        this._state = new StoppedState(this);
        this._baseStrategy = new SimplePlayerStrategy(this.queueManager, this);
        this._strategy = new NoneLoopDecorator(this._baseStrategy, this.queueManager, this);

        this.mediaSession = new MediaSessionController({
            play: () => this.play(),
            pause: () => this.pause(),
            stop: () => this.stop(),
            next: () => this.next(),
            previous: () => this.previous(),
            seekTo: seconds => this.seek(seconds),
            getPosition: () => this.engine.getPosition(),
            getDuration: () => this.track?.duration ?? 0,
        });
        this.mediaSession.attachHandlers();
    }

    public static getInstance(): Player {
        if (!this.uniqueInstance) {
            this.uniqueInstance = new Player();
        }

        return this.uniqueInstance;
    }

    public get token(): string | null {
        return this.lastToken;
    }

    public get volume(): number {
        return this.volumeLevel;
    }

    public setVolume(value: number): void {
        this.volumeLevel = Math.max(0, Math.min(1, value));
        this.engine.setVolume(this.volumeLevel);
    }

    public get currentIndex(): number {
        return this.queueManager.currentIndex;
    }

    public set currentIndex(value: number) {
        this.queueManager.currentIndex = value;
    }

    get state(): MachinePlayerState {
        return this._state;
    }

    transitionTo(state: MachinePlayerState): void {
        this._state = state;
        this.notify();
    }

    get track(): TrackDTO | undefined {
        return this._strategy.track;
    }

    get queue(): TrackDTO[] {
        return this._strategy.queue;
    }

    public destroy(): void {
        this.loadGeneration += 1;
        this.stopHeartbeat();
        this.stopBufferWatch();
        this.tokenService.cancelPending();
        this.engine.unload();
    }

    public isPlaying(): boolean {
        return this.state instanceof PlayingState;
    }

    public isPaused(): boolean {
        return this.state instanceof PausedState;
    }

    public isLoading(): boolean {
        return this.state instanceof LoadingState;
    }

    public isStopped(): boolean {
        return this.state instanceof StoppedState;
    }

    public attach(observer: Observer): void {
        this.observers.push(observer);
    }

    public detach(observer: Observer): void {
        this.observers = this.observers.filter(o => o !== observer);
    }

    public notify(): void {
        this.observers.forEach(o => o.update());
    }

    public play(): void {
        this._state.play();
    }

    public pause(): void {
        this._state.pause();
    }

    public stop(): void {
        this.loadGeneration += 1;
        this.stopHeartbeat();
        this.stopBufferWatch();
        this._state.stop();
    }

    public load(): void {
        this.transitionTo(new LoadingState(this));
    }

    public seek(num?: number): number | undefined {
        if (num !== undefined) {
            const applied = this.engine.seekTo(num);

            if (applied) {
                this.play();
            }
            return undefined;
        }

        return this.engine.getPosition();
    }

    public setTrack(track: TrackDTO, queue: TrackDTO[], autoplay: boolean = true): void {
        this.tokenRetries = 0;

        const isCurrentTrack = this.pendingTrackId === track.id &&
            (this.engine.loaded || this.isLoading());

        this._strategy.execute(track, queue);

        if (isCurrentTrack) {
            if (autoplay && !this.isPlaying()) {
                this.play();
            }

            return;
        }

        this.pendingTrackId = track.id;
        void this.loadTrack(track, track.start ?? 0, autoplay);
    }

    public setStrategy(strategy: string): void {
        switch (strategy) {
            case "simple":
                this._baseStrategy = new SimplePlayerStrategy(this.queueManager, this);
                this._strategy.wrapped = this._baseStrategy;
                break;
            case "shuffle":
                this._baseStrategy = new ShufflePlayerStrategy(this.queueManager, this);
                this._strategy.wrapped = this._baseStrategy;
                break;
            case "loopPlaylist":
                this._strategy = new PlaylistLoopDecorator(this._baseStrategy, this.queueManager, this);
                break;
            case "loopTrack":
                this._strategy = new TrackLoopDecorator(this._baseStrategy, this.queueManager, this);
                break;
            case "noneLoop":
                this._strategy = new NoneLoopDecorator(this._baseStrategy, this.queueManager, this);
                break;
        }
    }

    public next(): void {
        this.stop();

        const nextIdx = this.queueManager.nextIndex();
        if (nextIdx === null) return;

        this.queueManager.currentIndex = nextIdx;

        const track = this.queueManager.current;
        if (track) void this.loadTrack(track);
    }

    public previous(): void {
        const position = this.engine.getPosition();

        if (position > PREVIOUS_TRACK_THRESHOLD_SEC) {
            this.seek(0);
            return;
        }

        const prevIdx = this.queueManager.previousIndex();
        if (prevIdx === null) return;

        this.queueManager.currentIndex = prevIdx;

        const track = this.queueManager.current;
        if (track) void this.loadTrack(track);
    }

    private async loadTrack(track: TrackDTO, startFrom: number = 0, autoplay: boolean = true): Promise<void> {
        const generation = ++this.loadGeneration;
        this.pendingTrackId = track.id;

        this.mediaSession.setMetadata(track);
        this.mediaSession.setPlaybackState("paused");
        this.transitionTo(new LoadingState(this));

        try {
            const token = await this.tokenService.fetchToken(track.id);

            if (generation !== this.loadGeneration) return;

            this.lastToken = token;
            this.startHeartbeat();

            const params = new URLSearchParams({
                token,
                duration: String(track.duration ?? "")
            });

            this.engine.load(
                {src: `/api/tracks?${params.toString()}`, volume: this.volumeLevel, startFrom},
                {
                    onLoad: () => {
                        if (generation !== this.loadGeneration) return;

                        if (autoplay && !this.isPaused()) this.play();
                        else this.pause();

                        this.mediaSession.updatePositionState();
                        this.startBufferWatch();
                    },
                    onPlay: () => {
                        if (generation !== this.loadGeneration) return;

                        this.mediaSession.setPlaybackState("playing");
                        this.startHeartbeat();
                    },
                    onPause: () => {
                        if (generation !== this.loadGeneration) return;

                        this.mediaSession.setPlaybackState("paused");
                    },
                    onEnd: () => {
                        if (generation !== this.loadGeneration) return;

                        this.mediaSession.setPlaybackState("none");
                        this.stopHeartbeat();
                        this.stopBufferWatch();

                        if (this.lastToken) {
                            this.tokenService.reportHeartbeat(this.lastToken, this.engine.getPosition());
                        }

                        this._strategy.onTrackEnd();
                    },
                    onLoadError: error => this.handlePlaybackError(error),
                    onPlayError: error => this.handlePlaybackError(error),
                }
            );
        } catch (error) {
            if (generation !== this.loadGeneration) return;

            console.error("Failed to fetch track token", error);
            this.scheduleReload(generation, track, startFrom, autoplay);
        }
    }

    private handlePlaybackError(error: unknown): void {
        this.stopHeartbeat();
        console.error("Playback error", error);

        const track = this.queueManager.current;
        if (!track || this.pendingTrackId !== track.id) return;

        const fallbackStart = this.lastPosition > 0 ? this.lastPosition : (track.start ?? 0);
        this.scheduleReload(this.loadGeneration, track, fallbackStart, !this.isPaused());
    }

    private scheduleReload(generation: number, track: TrackDTO, startFrom: number, autoplay: boolean): void {
        this.tokenRetries += 1;

        if (this.tokenRetries > MAX_TOKEN_RETRIES) return;

        setTimeout(() => {
            if (generation === this.loadGeneration && this.pendingTrackId === track.id) {
                void this.loadTrack(track, startFrom, autoplay);
            }
        }, TOKEN_RETRY_DELAY_MS);
    }

    private startHeartbeat(): void {
        this.stopHeartbeat();

        this.heartbeatTimer = setInterval(() => {
            if (this.isStopped() || !this.lastToken) return;

            this.tokenService.reportHeartbeat(this.lastToken, this.engine.getPosition());
        }, HEARTBEAT_INTERVAL_MS);
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    private startBufferWatch(): void {
        this.stopBufferWatch();
        this.lastPosition = -1;
        this.stalledPolls = 0;

        this.bufferTimer = setInterval(() => {
            if (!this.isPlaying()) return;

            const position = this.engine.getPosition();

            if (position === this.lastPosition) {
                this.stalledPolls += 1;

                if (this.stalledPolls >= BUFFER_STALL_POLLS && !this.isLoading()) {
                    this.transitionTo(new LoadingState(this));
                }
            } else {
                this.stalledPolls = 0;

                if (this.isLoading()) {
                    this.transitionTo(new PlayingState(this));
                }

                this.lastPosition = position;
            }
        }, BUFFER_POLL_INTERVAL_MS);
    }

    private stopBufferWatch(): void {
        if (this.bufferTimer) {
            clearInterval(this.bufferTimer);
            this.bufferTimer = null;
        }
    }
}
